/*
 * Core component class for the toolchain.
 */
import React from 'react';

export default class CoreComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
        this._componentTimers = [];
        try {
            if (ENVConfig.GA_TRACKING.enabled) {
                let Analytics = require('../libs/GoogleAnalytics');
                this.Analytics = {
                    page: (page, options = {}) => Analytics.page(page, options),
                    modal: (modal, options = {}) => Analytics.modal(modal, options),
                    event: (category, action, label, value, nonInteraction = true, transport = 'beacon') => Analytics.event({ category, action, label, value, nonInteraction, transport }),
                    eventObj: (obj = {}) => Analytics.event(obj),
                    timing: (category, variable, value, label) => Analytics.timing({ category, variable, value, label }),
                    exception: (description, fatal = false) => Analytics.exception({ description, fatal }),

                    // Direct link to Google Analytics ga() command.
                    // https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference
                    ga: Analytics.ga,
                }
            }
        } catch { /* GA Disabled */ }
    }

    /*
     * Force a component to update by setting a random key
     * to it's state. Allow a forced update with a promise.
     */
    safeForceUpdate(key = '__FORCE_UPDATE__', value = Date.now()) {
        return this.setStatePrev({ [key]: value }).catch(() => { /* Component was unmounted. */ });
    }

    /*
     * Clear timers by default. If you use componentWillUnmount() in an
     * extended class you must also call this.clearTimeouts() there if you use
     * this.setTimeout() to prevent thrown errors/warnings.
     */
    componentWillUnmount() {
        this.__calledComponentWillUnmount = true;
        this.clearTimeouts();
    }

    /*
     * Allow for appending values based on the previous state.
     */
    setStatePrev(obj, old = false) {
        if (!this.__calledComponentWillUnmount) {
            return new Promise((resolve, reject) => {
                try {
                    this.setState((prevState) => {
                        let previous = Object.assign({}, old || prevState),
                            update = Object.assign(old ? old : prevState, obj);
                        resolve({ update, previous });
                        return update;
                    });
                } catch (error) { reject(error); }
            });
        }
        return Promise.reject(new Error('Component has been unmounted.'));
    }

    /*
     * Using setStatePrev to toggle state entries by key names,
     * allowing custom toggle values in argument[1].
     */
    toggleStates(keys = [], togs = [true, false]) {
        return this.setStatePrev((Array.isArray(keys) ? keys : [keys]).reduce((acc, _key) => {
            let key = _key;
            if (Array.isArray(key)) {
                [key, togs] = _key;
            }

            return {
                ...acc,
                [key]: (togs[1] === this.state[key] ? togs[0] : togs[1]),
            };
        }, {}));
    }

    /*
     * Check if a state key is toggled, based on whatever values they should contain.
     * Second argument is an array of toggled states. Defaults to booleans.
     * Returns 1 if toggled, 0 is untoggled, -1 if it does not match any toggle state.
     */
    toggled(key, togs = [true, false]) {
        return (this.state[key] === togs[0] ? 1 : (this.state[key] === togs[1] ? 0 : -1));
    }

    isToggled() {
        return Boolean(this.toggled(...arguments));
    }

    /*
     *  Use this.setTimeout() for things like calling setState in a delayed manor,
     *  as not removing timers will throw errors if the component is unmounted.
     *  Use this.clearTimeouts() in componentWillUnmount() to prevent these errors.
     */
    setTimeout(fn = () => { }, time = 1) {
        let timeout = setTimeout(() => {
            try { fn(); } catch (error) {
                console.warn('[TIMER FAILED]', error.message);
            }
        }, time);
        this._componentTimers.push(timeout);

        return timeout;
    }

    clearTimeouts() {
        this.__calledComponentWillUnmount = true;
        this._componentTimers.forEach(clearTimeout);
        this._componentTimers = [];
    }

    /*
     * Delete specified keys from an Object.
     */
    cleanObject(obj, keys = []) {
        return (Array.isArray(keys) ? keys : [keys]).reduce((a, c) => (delete a[c] && a), { ...(obj || {}) });
    }
}
